import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { CalendarDaysIcon, ClockIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useRouteLoaderData } from '@remix-run/react';
import { captureException } from '@sentry/remix';
import { MobileModalElement, ModalElement } from '@shared/dream-components/src';
import { useQueryClient } from '@tanstack/react-query';

import Button from '~/components/Button';
import useIsMobile from '~/hooks/useIsMobile';
import useWebinarDetails, { webinarDetailsKeys } from '~/hooks/useLoaders/useWebinarDetails';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { CalComFulfillmentMetadata } from '~/interfaces/fulfillment';
import type { RootLoaderData } from '~/root';
import { CAL_COM_API_URL } from '~/utils/cal_com/constants';
import { generateWebinarAccessToken } from '~/utils/generateWebinarAccessToken';
import { getLocalTimeZone } from '~/utils/getLocalTimeZone';

const CAL_API_VERSION = '2024-06-14';

interface BookWebinarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBookingSuccess: (bookingData: CalComBooking, webinarAccessToken: string) => void;
  shouldDisableConfirmButton?: boolean;
  productSlug: string;
  eventName: string;
  eventStartsAt: string;
  eventEndsAt?: string;
  eventTimeZone: string;
  remainingSeats?: number;
  maxSeats?: number;
  calComEventTypeId: number;
  calComClientId: string;
  currentSubscriberEmail?: string;
  currentSubscriberName?: string;
  fulfillmentMetadata?: CalComFulfillmentMetadata;
  onBookingAlreadyExists?: () => void;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDate(startIso: string, timeZone: string): string {
  const start = new Date(startIso);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone,
  }).format(start);
}

function formatTimeRange(startIso: string, endIso: string | undefined, timeZone: string): string {
  const start = new Date(startIso);
  const startTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
  }).format(start);

  if (endIso) {
    const end = new Date(endIso);
    const endTime = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(end);
    return `${startTime} - ${endTime}`;
  }

  return startTime;
}

function formatDuration(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const minutes = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

function buildWebinarJoinUrl(productSlug: string, publicationUrl?: string): string {
  if (publicationUrl) {
    const normalizedPublicationUrl = publicationUrl.replace(/\/$/, '');
    return `${normalizedPublicationUrl}/webinars/join/${productSlug}`;
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}/webinars/join/${productSlug}`;
  }

  return `/webinars/join/${productSlug}`;
}

// ---------------------------------------------------------------------------
// Seats badge
// ---------------------------------------------------------------------------

function SeatsBadge({ remaining, max, label }: { remaining?: number; max?: number; label: string }) {
  if (remaining == null || max == null) return null;

  const ratio = remaining / max;
  const isUrgent = ratio <= 0.1 || remaining <= 5;
  const isWarning = ratio <= 0.2 || remaining <= 10;

  let bgClass: string;
  let textClass: string;
  let dotClass: string;

  if (isUrgent) {
    bgClass = 'bg-red-50';
    textClass = 'text-red-700';
    dotClass = 'bg-red-500';
  } else if (isWarning) {
    bgClass = 'bg-amber-50';
    textClass = 'text-amber-700';
    dotClass = 'bg-amber-500';
  } else {
    bgClass = 'bg-emerald-50';
    textClass = 'text-emerald-700';
    dotClass = 'bg-emerald-500';
  }

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${bgClass} ${textClass} text-xs font-medium w-fit`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <UsersIcon className="w-3.5 h-3.5" />
      <span>
        {remaining} / {max} {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BookWebinarModal = ({
  isOpen,
  onClose,
  onBookingSuccess,
  shouldDisableConfirmButton = false,
  productSlug,
  eventName,
  eventStartsAt,
  eventEndsAt,
  eventTimeZone,
  remainingSeats,
  maxSeats,
  calComEventTypeId,
  calComClientId,
  currentSubscriberEmail,
  currentSubscriberName,
  fulfillmentMetadata,
  onBookingAlreadyExists,
}: BookWebinarModalProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const isRescheduling = Boolean(fulfillmentMetadata?.cal_booking_uid);
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const publication = rootData?.publication;

  const { data: freshData } = useWebinarDetails({ slug: productSlug, enabled: isOpen });

  const displayRemainingSeats = freshData?.remaining_seats ?? remainingSeats;
  const displayMaxSeats = freshData?.max_seats ?? maxSeats;
  const displayStartsAt = freshData?.event_starts_at ?? eventStartsAt;
  const displayEndsAt = freshData?.event_ends_at ?? eventEndsAt;
  const displayTimeZone = getLocalTimeZone(freshData?.iana_time_zone ?? freshData?.time_zone ?? eventTimeZone);

  const initialEmail = fulfillmentMetadata?.cal_booking_fields_responses?.email ?? currentSubscriberEmail ?? '';
  const initialName = fulfillmentMetadata?.cal_booking_fields_responses?.name ?? currentSubscriberName ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync form state when modal reopens with different subscriber data
  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setName(initialName);
      setIsSubmitting(false);
    }
  }, [isOpen, initialEmail, initialName]);

  const isEmailLocked = Boolean(currentSubscriberEmail);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email.trim() || !name.trim()) {
        return;
      }

      setIsSubmitting(true);
      const webinarAccessToken = generateWebinarAccessToken();
      const calendarLocationUrl = buildWebinarJoinUrl(productSlug, publication?.url);

      try {
        const payload: Record<string, unknown> = {
          start: displayStartsAt,
          eventTypeId: calComEventTypeId,
          responses: {
            email: email.trim(),
            name: name.trim(),
            guests: [],
            location: { value: calendarLocationUrl, optionValue: calendarLocationUrl },
            notes: '',
          },
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language?.split('-')[0] || 'en',
          metadata: {},
        };

        if (fulfillmentMetadata?.cal_booking_uid) {
          payload.rescheduleUid = fulfillmentMetadata.cal_booking_uid;
        }

        const response = await fetch(`${CAL_COM_API_URL}/bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-cal-client-id': calComClientId,
            'cal-api-version': CAL_API_VERSION,
          },
          body: JSON.stringify(payload),
        });

        const json = await response.json();

        if (!response.ok) {
          if (response.status === 409) {
            if (onBookingAlreadyExists) {
              onBookingAlreadyExists();
            } else {
              const conflictMessage = json?.error?.message || json?.message || json?.details;
              toast.error(conflictMessage || t('products.error.booking_conflict'));
            }
            return;
          }

          const errorMessage = json?.error?.message || json?.message || json?.details;
          captureException(new Error(`Booking failed (${response.status}): ${errorMessage || JSON.stringify(json)}`));
          toast.error(
            errorMessage ||
              t('products.error.booking_failed', { defaultValue: 'Something went wrong, please try again' }),
          );
          return;
        }

        if (json?.status === 'success' && json?.data?.uid) {
          const bookingData: CalComBooking = {
            uid: json.data.uid,
            attendees: [{ email: email.trim(), name: name.trim() }],
            bookingFieldsResponses: json.data.responses ?? {
              email: email.trim(),
              guests: [],
              location: { value: '', optionValue: '' },
              name: name.trim(),
              notes: '',
            },
          };
          queryClient.invalidateQueries({ queryKey: webinarDetailsKeys.bySlug(productSlug) });
          onBookingSuccess(bookingData, webinarAccessToken);
        } else {
          captureException(new Error(`Unexpected booking response: ${JSON.stringify(json)}`));
          toast.error(t('products.error.booking_failed', { defaultValue: 'Something went wrong, please try again' }));
        }
      } catch (error) {
        captureException(error instanceof Error ? error : new Error(String(error)));
        toast.error(t('products.error.booking_failed', { defaultValue: 'Something went wrong, please try again' }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      email,
      name,
      displayStartsAt,
      calComEventTypeId,
      calComClientId,
      publication?.url,
      fulfillmentMetadata,
      onBookingAlreadyExists,
      onBookingSuccess,
      productSlug,
      queryClient,
      t,
    ],
  );

  if (!isOpen) {
    return null;
  }

  const datePart = formatDate(displayStartsAt, displayTimeZone);
  const timePart = formatTimeRange(displayStartsAt, displayEndsAt, displayTimeZone);
  const duration = formatDuration(displayStartsAt, displayEndsAt);
  const isDisabled = isSubmitting || shouldDisableConfirmButton;

  // -------------------------------------------------------------------------
  // Event details panel (left on desktop, top on mobile)
  // -------------------------------------------------------------------------

  const eventDetailsPanel = (
    <div className="flex flex-col gap-6 p-6 md:p-8 md:w-[280px] md:min-w-[280px] bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200">
      {/* Publication identity */}
      {publication && (
        <div className="flex items-center gap-2.5">
          {publication.logo?.thumb?.url && (
            <img
              src={publication.logo.thumb.url}
              alt={publication.name}
              className="w-8 h-8 rounded-full object-cover ring-1 ring-gray-200"
            />
          )}
          <span className="text-sm text-gray-500 font-medium truncate">{publication.name}</span>
        </div>
      )}

      {/* Event title */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 leading-snug">
          {isRescheduling ? t('products.webinar.reschedule_title', { defaultValue: 'Reschedule webinar' }) : eventName}
        </h2>
        {duration && <p className="text-sm text-gray-400 mt-1">{duration}</p>}
      </div>

      {/* Date & time */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2.5">
          <CalendarDaysIcon className="w-[18px] h-[18px] text-gray-400 mt-0.5 shrink-0" />
          <span className="text-sm text-gray-600">{datePart}</span>
        </div>
        <div className="flex items-start gap-2.5">
          <ClockIcon className="w-[18px] h-[18px] text-gray-400 mt-0.5 shrink-0" />
          <span className="text-sm text-gray-600">{timePart}</span>
        </div>
      </div>

      {/* Seats badge */}
      <SeatsBadge
        remaining={displayRemainingSeats}
        max={displayMaxSeats}
        label={t('products.webinar.seats_available', { defaultValue: 'seats available' })}
      />
    </div>
  );

  // -------------------------------------------------------------------------
  // Booking form (right on desktop, bottom on mobile)
  // -------------------------------------------------------------------------

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500';

  const bookingForm = (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6 md:p-8 md:w-[320px] bg-white">
      <div className="space-y-1.5">
        <label htmlFor="booking-name" className="block text-sm font-medium text-gray-700">
          {t('products.webinar.name_label', { defaultValue: 'Name' })}
        </label>
        <input
          id="booking-name"
          name="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className={inputClasses}
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="booking-email" className="block text-sm font-medium text-gray-700">
          {t('products.webinar.email_label', { defaultValue: 'Email' })}
        </label>
        <input
          id="booking-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isEmailLocked}
          required
          className={inputClasses}
        />
      </div>

      <div className="[&_button]:!rounded-md">
        <Button type="submit" variant="primary" block disabled={isDisabled} isLoading={isSubmitting}>
          {isRescheduling
            ? t('products.webinar.reschedule_button', { defaultValue: 'Reschedule' })
            : t('products.webinar.register_button', { defaultValue: 'Register' })}
        </Button>
      </div>
    </form>
  );

  // -------------------------------------------------------------------------
  // Combined content
  // -------------------------------------------------------------------------

  const modalContent = (
    <div className="flex flex-col md:flex-row bg-white overflow-hidden">
      {eventDetailsPanel}
      {bookingForm}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <MobileModalElement
        isOpen={isOpen}
        onOpenChange={onClose}
        isLoading={isDisabled}
        showCloseButton={!isDisabled}
        hideOverflow={false}
        element={{
          type: 'mobileModal',
          content: [],
        }}
      >
        {modalContent}
      </MobileModalElement>,
      document.body,
    );
  }

  return createPortal(
    <ModalElement
      isOpen={isOpen}
      onOpenChange={onClose}
      isLoading={isDisabled}
      showCloseButton
      hideOverflow={false}
      element={{
        type: 'modal',
        content: [],
      }}
    >
      {modalContent}
    </ModalElement>,
    document.body,
  );
};
